import React, { useState, useEffect } from 'react';
import {
  Box, Stepper, Step, StepLabel, Button, Typography, Container, Card, CardContent,
  TextField, Grid, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Fade, Slide,
  Divider, List, ListItem, ListItemText, ListItemIcon, Alert, CircularProgress, Link, Tooltip
} from '@mui/material';
import {
  Add, Delete, Edit, CheckCircle, Assignment, Description, Preview, Save, ArrowBack, ArrowForward, GetApp
} from '@mui/icons-material';
import { v4 as uuidv4 } from 'uuid';
import { addDoc, collection, doc, updateDoc, getDoc } from 'firebase/firestore';
import { firestore as db } from './firebase.config';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';

const steps = [
  { label: 'Basic Information', description: 'Enter checklist name, description, and remarks', icon: <Description /> },
  { label: 'Add Sections', description: 'Create sections to organize your checklist', icon: <Assignment /> },
  { label: 'Add Rows', description: 'Add requirements and observations to sections', icon: <Add /> },
  { label: 'Review & Save', description: 'Review your checklist and save', icon: <Preview /> }
];

const CreateChecklistWizard = ({ selectedChecklist, onClose }) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);

  // States for wizard data
  const [checklistName, setChecklistName] = useState('');
  const [description, setDescription] = useState('');
  const [remarks, setRemarks] = useState('');
  const [sections, setSections] = useState([]);

  // Dialog states
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [currentSection, setCurrentSection] = useState({ title: '', type: 'A' });
  const [editingSectionId, setEditingSectionId] = useState(null);
  const [selectedSectionId, setSelectedSectionId] = useState(null);
  const [rowDialogOpen, setRowDialogOpen] = useState(false);
  const [currentRow, setCurrentRow] = useState({
    srNo: '', mmesRequirements: '', guideline: '', observation: '', complianceDetails: '',
    suggestion: '', maxScore: '', auditScore: '', riskZone: 'green', attachments: [], compliance: 'pending'
  });
  const [editingRowId, setEditingRowId] = useState(null);

  const [alert, setAlert] = useState({ show: false, message: '', severity: 'success' });

  // Read from AuthContext — single source of truth.
  const { currentUser, tenantId } = useAuth();
  const userEmail = currentUser?.email || '';

  useEffect(() => {
    if (selectedChecklist) {
      setChecklistName(selectedChecklist.name || '');
      setDescription(selectedChecklist.description || '');
      setRemarks(selectedChecklist.remarks || '');

      const findColId = (namePart) => {
        const col = selectedChecklist.columns?.find(c => c.name.toLowerCase().includes(namePart.toLowerCase()));
        return col ? col.id : null;
      };

      const colIdMap = {
        srNo: findColId('sr#'),
        mmesRequirements: findColId('mmes requirements'),
        guideline: findColId('guideline'),
        observation: findColId('observation'),
        complianceDetails: findColId('compliance details'),
        suggestion: findColId('suggestion'),
        maxScore: findColId('max. score'),
        auditScore: findColId('audit score'),
        riskZone: findColId('risk zone'),
        attachments: findColId('attachments'),
      };

      const transformedSections = selectedChecklist.sections?.map(section => ({
        id: section.id,
        title: section.title,
        type: section.type,
        rows: section.rows?.map(row => {
          const oldAttachments = row.data[colIdMap.attachments];
          const attachmentUrls = oldAttachments && typeof oldAttachments === 'object'
            ? Object.values(oldAttachments).filter(url => typeof url === 'string' && url.startsWith('http'))
            : [];

          const newRowData = {
            srNo: row.data[colIdMap.srNo] || '',
            mmesRequirements: row.data[colIdMap.mmesRequirements] || '',
            guideline: row.data[colIdMap.guideline] || '',
            observation: row.data[colIdMap.observation] || '',
            complianceDetails: row.data[colIdMap.complianceDetails] || '',
            suggestion: row.data[colIdMap.suggestion] || '',
            maxScore: row.data[colIdMap.maxScore] || '',
            auditScore: row.data[colIdMap.auditScore] || '',
            riskZone: (row.data[colIdMap.riskZone] || 'green').toLowerCase(),
            attachments: attachmentUrls,
            compliance: 'pending',
          };

          return { id: row.id, data: newRowData };
        }) || []
      }));

      setSections(transformedSections || []);
    }
  }, [selectedChecklist]);

  const showAlert = (message, severity = 'success') => {
    setAlert({ show: true, message, severity });
    setTimeout(() => setAlert({ show: false, message: '', severity: 'success' }), 4000);
  };

  const handleNext = () => {
    if (activeStep === 0 && !checklistName.trim()) {
      showAlert('Please enter a checklist name', 'error');
      return;
    }
    if (activeStep === 1 && sections.length === 0) {
      showAlert('Please add at least one section', 'error');
      return;
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleAddSection = () => {
    if (!currentSection.title.trim()) {
      showAlert('Please enter a section title', 'error');
      return;
    }

    if (editingSectionId) {
      setSections(sections.map(section =>
        section.id === editingSectionId
          ? { ...section, ...currentSection }
          : section
      ));
      setEditingSectionId(null);
    } else {
      const newSection = {
        id: uuidv4(),
        ...currentSection,
        rows: []
      };
      setSections([...sections, newSection]);
    }

    setCurrentSection({ title: '', type: String.fromCharCode(65 + sections.length) });
    setSectionDialogOpen(false);
  };

  const handleEditSection = (section) => {
    setCurrentSection({ title: section.title, type: section.type });
    setEditingSectionId(section.id);
    setSectionDialogOpen(true);
  };

  const handleDeleteSection = (sectionId) => {
    setSections(sections.filter(section => section.id !== sectionId));
  };

  const handleAddRow = () => {
    if (!selectedSectionId) {
      showAlert('Please select a section first', 'error');
      return;
    }
    if (!currentRow.mmesRequirements.trim()) {
      showAlert('Please enter MMES Requirements', 'error');
      return;
    }

    const updatedSections = sections.map(section => {
      if (section.id === selectedSectionId) {
        const newRow = {
          id: editingRowId || uuidv4(),
          data: { ...currentRow }
        };

        if (editingRowId) {
          return {
            ...section,
            rows: section.rows.map(row =>
              row.id === editingRowId ? newRow : row
            )
          };
        } else {
          return {
            ...section,
            rows: [...(section.rows || []), newRow]
          };
        }
      }
      return section;
    });

    setSections(updatedSections);
    setCurrentRow({
      srNo: '', mmesRequirements: '', guideline: '', observation: '', complianceDetails: '',
      suggestion: '', maxScore: '', auditScore: '', riskZone: 'green', attachments: [], compliance: 'pending'
    });
    setEditingRowId(null);
    setRowDialogOpen(false);
  };

  const handleEditRow = (sectionId, row) => {
    setSelectedSectionId(sectionId);
    setCurrentRow(row.data);
    setEditingRowId(row.id);
    setRowDialogOpen(true);
  };

  const handleDeleteRow = (sectionId, rowId) => {
    const updatedSections = sections.map(section => {
      if (section.id === sectionId) {
        return {
          ...section,
          rows: section.rows.filter(row => row.id !== rowId)
        };
      }
      return section;
    });
    setSections(updatedSections);
  };

  const handleSaveChecklist = async () => {
    setLoading(true);
    try {
      const checklistData = {
        name: checklistName,
        description,
        remarks,
        sections,
        createdBy: userEmail,
        tenantId: tenantId || null,
        createdAt: new Date(),
        updatedAt: new Date(),
        status: 'active',
        columns: selectedChecklist?.columns || [
            { id: uuidv4(), name: "Sr#" },
            { id: uuidv4(), name: "MMES Requirements of 3P" },
            { id: uuidv4(), name: "Guideline prompts" },
            { id: uuidv4(), name: "Observation" },
            { id: uuidv4(), name: "Compliance details" },
            { id: uuidv4(), name: "Suggestion" },
            { id: uuidv4(), name: "Max. score" },
            { id: uuidv4(), name: "Audit score" },
            { id: uuidv4(), name: "Risk Zone R/Y/G" },
            { id: uuidv4(), name: "Attachments" },
        ]
      };

      if (selectedChecklist?.id || id) {
        const checklistId = selectedChecklist?.id || id;
        await updateDoc(doc(db, 'auditchecklists', checklistId), {
          ...checklistData,
          updatedAt: new Date()
        });
        showAlert('Checklist updated successfully!', 'success');
      } else {
        await addDoc(collection(db, 'auditchecklists'), checklistData);
        showAlert('Checklist created successfully!', 'success');
      }

      setTimeout(() => {
        if (onClose) {
          onClose();
        } else {
          navigate('/admin');
        }
      }, 1500);
    } catch (error) {
      console.error('Error saving checklist:', error);
      showAlert('Error saving checklist. Please try again.', 'error');
    } finally {
      setLoading(false);
    }
  };


  const renderStepContent = (step) => {
    switch (step) {
      case 0: // Basic Information
        return (
          <Fade in={true} timeout={600}>
            <Card sx={{ mt: 3 }}><CardContent sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom>Basic Information</Typography>
              <Grid container spacing={3} mt={1}>
                <Grid item xs={12}>
                  <TextField fullWidth label="Checklist Name *" value={checklistName} onChange={(e) => setChecklistName(e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Description" value={description} onChange={(e) => setDescription(e.target.value)} multiline rows={3} />
                </Grid>
                <Grid item xs={12}>
                  <TextField fullWidth label="Remarks" value={remarks} onChange={(e) => setRemarks(e.target.value)} multiline rows={2} />
                </Grid>
              </Grid>
            </CardContent></Card>
          </Fade>
        );

      case 1: // Add Sections
        return (
          <Fade in={true} timeout={600}>
            <Card sx={{ mt: 3 }}><CardContent sx={{ p: 4 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5">Add Sections</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => setSectionDialogOpen(true)}>Add Section</Button>
              </Box>
              <Grid container spacing={2}>
                {sections.map((section) => (
                  <Grid item xs={12} sm={6} md={4} key={section.id}>
                    <Card variant="outlined"><CardContent>
                      <Box display="flex" justifyContent="space-between">
                        <Chip label={`Section ${section.type}`} size="small" color="primary" />
                        <Box>
                          <IconButton size="small" onClick={() => handleEditSection(section)}><Edit fontSize="small" /></IconButton>
                          <IconButton size="small" color="error" onClick={() => handleDeleteSection(section.id)}><Delete fontSize="small" /></IconButton>
                        </Box>
                      </Box>
                      <Typography variant="h6" mt={1}>{section.title}</Typography>
                      <Typography variant="body2" color="text.secondary">{section.rows?.length || 0} rows</Typography>
                    </CardContent></Card>
                  </Grid>
                ))}
              </Grid>
            </CardContent></Card>
          </Fade>
        );

      case 2: // Add Rows
        return (
          <Fade in={true} timeout={600}>
            <Card sx={{ mt: 3 }}><CardContent sx={{ p: 4 }}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h5">Add Rows to Sections</Typography>
                <Button variant="contained" startIcon={<Add />} onClick={() => {
                  setSelectedSectionId(sections.length > 0 ? sections[0].id : null);
                  setCurrentRow({
                    srNo: '', mmesRequirements: '', guideline: '', observation: '', complianceDetails: '',
                    suggestion: '', maxScore: '', auditScore: '', riskZone: 'green', attachments: [], compliance: 'pending'
                  });
                  setEditingRowId(null);
                  setRowDialogOpen(true);
                }} disabled={sections.length === 0}>Add Row</Button>
              </Box>
              {sections.length === 0 && (
                  <Alert severity="info">Please add sections first in the "Add Sections" step to add rows.</Alert>
              )}
              {sections.map((section) => (
                <Box key={section.id} mb={4}>
                  <Box sx={{ backgroundColor: '#FFD700', p: 1.5, borderRadius: 1, mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', color: '#000' }}>{section.type}. {section.title}</Typography>
                  </Box>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: 'grey.200' }}>
                          <TableCell sx={{ fontWeight: 'bold' }}>Sr#</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>MMES Requirements of 3P</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Guideline prompts</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Observation</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Compliance details</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Suggestion</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Max. score</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Audit score</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Risk Zone</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }}>Attachments</TableCell>
                          <TableCell sx={{ fontWeight: 'bold' }} align="center">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {section.rows?.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={11} align="center" sx={{ py: 2, color: 'text.secondary' }}>
                              No rows added to this section yet.
                            </TableCell>
                          </TableRow>
                        ) : (
                          section.rows?.map((row) => (
                            <TableRow key={row.id}>
                              <TableCell>{row.data.srNo}</TableCell>
                              <TableCell>{row.data.mmesRequirements}</TableCell>
                              <TableCell>{row.data.guideline}</TableCell>
                              <TableCell>{row.data.observation}</TableCell>
                              <TableCell>{row.data.complianceDetails}</TableCell>
                              <TableCell>{row.data.suggestion}</TableCell>
                              <TableCell>{row.data.maxScore}</TableCell>
                              <TableCell>{row.data.auditScore}</TableCell>

                              {/* ✅ --- START: MODIFIED RISK ZONE CELL --- */}
                              <TableCell>
                                <Chip
                                  label={row.data.riskZone}
                                  size="small"
                                  sx={{
                                    backgroundColor:
                                      row.data.riskZone === 'red' ? '#f44336' :
                                      row.data.riskZone === 'yellow' ? '#ff9800' :
                                      '#4caf50', // green
                                    color: 'white',
                                    textTransform: 'capitalize',
                                    fontWeight: 'bold',
                                    minWidth: '60px'
                                  }}
                                />
                              </TableCell>
                              {/* ✅ --- END: MODIFIED RISK ZONE CELL --- */}

                              <TableCell>{row.data.attachments?.length || 0} files</TableCell>
                              <TableCell align="center">
                                <IconButton size="small" onClick={() => handleEditRow(section.id, row)}><Edit fontSize="small" /></IconButton>
                                <IconButton size="small" color="error" onClick={() => handleDeleteRow(section.id, row.id)}><Delete fontSize="small" /></IconButton>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              ))}
            </CardContent></Card>
          </Fade>
        );

      case 3: // Review & Save
        return (
          <Fade in={true} timeout={600}>
            <Card sx={{ mt: 3 }}><CardContent sx={{ p: 4 }}>
              <Typography variant="h5" gutterBottom>Review & Save</Typography>
              <Grid container spacing={4} mt={1}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined"><CardContent>
                    <Box display="flex" justifyContent="space-between" alignItems="center">
                      <Typography variant="h6">Basic Information</Typography>
                      {/* ✅ ADDED EDIT BUTTON FOR SUMMARY */}
                      <Tooltip title="Edit Basic Info">
                        <IconButton onClick={() => setActiveStep(0)} color="primary" size="small">
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <List dense>
                      <ListItem><ListItemText primary="Name" secondary={checklistName || 'N/A'} /></ListItem>
                      <ListItem><ListItemText primary="Description" secondary={description || 'N/A'} /></ListItem>
                    </List>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined"><CardContent>
                     <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Typography variant="h6">Structure Summary</Typography>
                        {/* ✅ ADDED EDIT BUTTON FOR STRUCTURE */}
                        <Tooltip title="Edit Sections">
                            <IconButton onClick={() => setActiveStep(1)} color="primary" size="small">
                              <Edit />
                            </IconButton>
                        </Tooltip>
                     </Box>
                    <List dense>
                      <ListItem><ListItemIcon><Assignment /></ListItemIcon><ListItemText primary={`${sections.length} Sections`} /></ListItem>
                      <ListItem><ListItemIcon><CheckCircle /></ListItemIcon><ListItemText primary={`${sections.reduce((total, s) => total + (s.rows?.length || 0), 0)} Total Rows`} /></ListItem>
                    </List>
                  </CardContent></Card>
                </Grid>
                <Grid item xs={12}>
                  <Card variant="outlined"><CardContent>
                    <Typography variant="h6">Sections Preview</Typography>
                    {sections.map((section) => (
                      <Box key={section.id} mt={2}>
                        <Typography variant="subtitle1" fontWeight={600}>{section.type}: {section.title}</Typography>
                        <Typography variant="body2" color="text.secondary">{section.rows?.length || 0} rows</Typography>
                        <Divider sx={{ mt: 1 }} />
                      </Box>
                    ))}
                  </CardContent></Card>
                </Grid>
              </Grid>
            </CardContent></Card>
          </Fade>
        );

      default:
        return 'Unknown step';
    }
  };

  // MAIN RETURN FOR THE WIZARD
  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={4}>
        <Typography variant="h3" gutterBottom>{selectedChecklist || id ? 'Edit Energy Report' : 'Create New Energy Report'}</Typography>
      </Box>
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 4 }}>
        {steps.map((step) => (
          <Step key={step.label}><StepLabel>{step.label}</StepLabel></Step>
        ))}
      </Stepper>
      {renderStepContent(activeStep)}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button disabled={activeStep === 0} onClick={handleBack} startIcon={<ArrowBack />}>Back</Button>
        {activeStep === steps.length - 1 ? (
          <Button variant="contained" onClick={handleSaveChecklist} startIcon={<Save />} disabled={loading}>
            {loading ? 'Saving...' : 'Save Checklist'}
          </Button>
        ) : (
          <Button variant="contained" onClick={handleNext} endIcon={<ArrowForward />}>Next</Button>
        )}
      </Box>

      {/* DIALOGS */}
      <Dialog open={sectionDialogOpen} onClose={() => setSectionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSectionId ? 'Edit Section' : 'Add New Section'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth margin="dense" label="Section Title" value={currentSection.title} onChange={(e) => setCurrentSection({ ...currentSection, title: e.target.value })} />
          <TextField fullWidth margin="dense" label="Section Type" value={currentSection.type} onChange={(e) => setCurrentSection({ ...currentSection, type: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSectionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddSection} variant="contained">{editingSectionId ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={rowDialogOpen} onClose={() => setRowDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingRowId ? 'Edit Row' : 'Add New Row'}</DialogTitle>
        <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                    <TextField fullWidth select label="Select Section" value={selectedSectionId || ''} onChange={(e) => setSelectedSectionId(e.target.value)} SelectProps={{ native: true }}>
                        <option value="">Choose a section</option>
                        {sections.map(s => <option key={s.id} value={s.id}>Section {s.type}: {s.title}</option>)}
                    </TextField>
                </Grid>
                <Grid item xs={12} sm={6}><TextField fullWidth label="Sr#" value={currentRow.srNo} onChange={(e) => setCurrentRow({ ...currentRow, srNo: e.target.value })}/></Grid>
                <Grid item xs={12}><TextField fullWidth label="MMES Requirements *" value={currentRow.mmesRequirements} onChange={(e) => setCurrentRow({ ...currentRow, mmesRequirements: e.target.value })} multiline rows={2} /></Grid>
                <Grid item xs={12}><TextField fullWidth label="Guideline Prompts" value={currentRow.guideline} onChange={(e) => setCurrentRow({ ...currentRow, guideline: e.target.value })} multiline rows={2}/></Grid>
                <Grid item xs={12}><TextField fullWidth label="Observation" value={currentRow.observation} onChange={(e) => setCurrentRow({ ...currentRow, observation: e.target.value })} multiline rows={2}/></Grid>
                <Grid item xs={12}><TextField fullWidth label="Compliance Details" value={currentRow.complianceDetails} onChange={(e) => setCurrentRow({ ...currentRow, complianceDetails: e.target.value })} multiline rows={2}/></Grid>
                <Grid item xs={12}><TextField fullWidth label="Suggestion" value={currentRow.suggestion} onChange={(e) => setCurrentRow({ ...currentRow, suggestion: e.target.value })} multiline rows={2}/></Grid>
                <Grid item xs={6} sm={3}><TextField fullWidth type="number" label="Max. Score" value={currentRow.maxScore} onChange={(e) => setCurrentRow({ ...currentRow, maxScore: e.target.value })}/></Grid>
                <Grid item xs={6} sm={3}><TextField fullWidth type="number" label="Audit Score" value={currentRow.auditScore} onChange={(e) => setCurrentRow({ ...currentRow, auditScore: e.target.value })}/></Grid>
                <Grid item xs={12} sm={6}>
                    <TextField fullWidth select label="Risk Zone" value={currentRow.riskZone} onChange={(e) => setCurrentRow({ ...currentRow, riskZone: e.target.value })} SelectProps={{ native: true }}>
                        <option value="green">Green</option>
                        <option value="yellow">Yellow</option>
                        <option value="red">Red</option>
                    </TextField>
                </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2">Existing Attachments</Typography>
                      {currentRow.attachments?.length === 0 ? (
                          <Typography variant="body2" color="text.secondary">No attachments</Typography>
                      ) : (
                          currentRow.attachments?.map((url, index) => (
                             <Link key={index} href={url} target="_blank" rel="noopener noreferrer" display="block">{url.split('/').pop().split('?')[0]}</Link>
                          ))
                      )}
                    </Grid>
            </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRowDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddRow} variant="contained">{editingRowId ? 'Update' : 'Add'}</Button>
        </DialogActions>
      </Dialog>

      {alert.show && <Alert severity={alert.severity} sx={{ position: 'fixed', bottom: 20, right: 20, zIndex: 9999 }}>{alert.message}</Alert>}
    </Container>
  );
};

export default CreateChecklistWizard;